"use client";

import { useState, useMemo } from "react";
import { Plus, LayoutGrid, List, Search } from "lucide-react";
import { StudioCard } from "./studio-card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import DataTable from "@/components/admin/shared/data-table";

interface Studio {
  id: number;
  name: string;
  capacity?: number | null;
  capacity_min?: number;
  capacity_max?: number;
  venue_id: number;
  status: string;
  equipment?: any;
  studio_infos?: Array<{
    lang: string;
    name?: string;
    information: string;
  }>;
  studio_medias?: Array<{
    url: string;
    type: string;
    is_primary: boolean;
  }>;
  _count?: {
    bookings?: number;
    shows?: number;
  };
}

interface StudioGridProps {
  studios: Studio[];
  venueId: number;
  venueName: string;
  onAddNew?: () => void;
  onEdit?: (studio: Studio) => void;
  onDuplicate?: (studio: Studio) => void;
  onDelete?: (studio: Studio) => void;
  canAdd?: boolean;
  canEdit?: boolean;
  canDuplicate?: boolean;
  canDelete?: boolean;
}

export default function StudioGrid({
  studios,
  venueId,
  venueName,
  onAddNew,
  onEdit,
  onDuplicate,
  onDelete,
  canAdd = true,
  canEdit = true,
  canDuplicate = true,
  canDelete = true,
}: StudioGridProps) {
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<"name" | "capacity">("name");

  // Filter and sort studios
  const filteredStudios = useMemo(() => {
    let filtered = studios;

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter((studio) =>
        studio.name.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((studio) => studio.status === statusFilter);
    }

    // Sort
    filtered = [...filtered].sort((a, b) => {
      if (sortBy === "name") {
        return a.name.localeCompare(b.name);
      } else if (sortBy === "capacity") {
        const aCapacity = a.capacity || a.capacity_max || 0;
        const bCapacity = b.capacity || b.capacity_max || 0;
        return aCapacity - bCapacity;
      }
      return 0;
    });

    return filtered;
  }, [studios, searchQuery, statusFilter, sortBy]);

  const tableColumns = [
    {
      key: "name" as keyof Studio,
      label: "Studio Name",
      sortable: true,
      render: (value: any) => <span className="font-medium">{value}</span>,
    },
    {
      key: "capacity" as keyof Studio,
      label: "Capacity",
      render: (_: any, studio: Studio) => {
        if (studio.capacity_min && studio.capacity_max) {
          return (
            <span>
              {studio.capacity_min}-{studio.capacity_max} people
            </span>
          );
        } else if (studio.capacity || studio.capacity_max) {
          return (
            <span>Up to {studio.capacity || studio.capacity_max} people</span>
          );
        }
        return <span>Not specified</span>;
      },
    },
    {
      key: "status" as keyof Studio,
      label: "Status",
      render: (value: any) => (
        <span
          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            value === "active"
              ? "bg-green-100 text-green-800"
              : value === "maintenance"
                ? "bg-yellow-100 text-yellow-800"
                : "bg-red-100 text-red-800"
          }`}
        >
          {value}
        </span>
      ),
    },
    {
      key: "_count" as keyof Studio,
      label: "Shows",
      width: "w-20",
      render: (value: any) => (
        <div className="text-center">{value?.shows || 0}</div>
      ),
    },
    {
      key: "_count" as keyof Studio,
      label: "Bookings",
      width: "w-20",
      render: (value: any) => (
        <div className="text-center">{value?.bookings || 0}</div>
      ),
    },
    {
      key: "id" as keyof Studio,
      label: "Actions",
      width: "w-32",
      render: (_: any, studio: Studio) => (
        <div className="flex gap-2 justify-end">
          {canEdit && (
            <Button variant="ghost" size="sm" onClick={() => onEdit?.(studio)}>
              Edit
            </Button>
          )}
          {canDuplicate && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDuplicate?.(studio)}
            >
              Duplicate
            </Button>
          )}
          {canDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete?.(studio)}
              className="text-red-600 hover:text-red-700"
            >
              Delete
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Studios</h1>
          <p className="text-muted-foreground">{venueName}</p>
        </div>
        {canAdd && (
          <Button onClick={onAddNew}>
            <Plus className="h-4 w-4 mr-2" />
            Add New Studio
          </Button>
        )}
      </div>

      {/* Filters and Controls */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search studios..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="maintenance">Maintenance</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={sortBy}
          onValueChange={(value) => setSortBy(value as "name" | "capacity")}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="name">Name</SelectItem>
            <SelectItem value="capacity">Capacity</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex gap-2">
          <Button
            variant={viewMode === "grid" ? "default" : "outline"}
            size="icon"
            onClick={() => setViewMode("grid")}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === "list" ? "default" : "outline"}
            size="icon"
            onClick={() => setViewMode("list")}
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="p-4 border rounded-lg">
          <h4 className="font-semibold mb-1">Total Studios</h4>
          <p className="text-2xl font-bold">{studios.length}</p>
        </div>
        <div className="p-4 border rounded-lg">
          <h4 className="font-semibold mb-1">Active Studios</h4>
          <p className="text-2xl font-bold">
            {studios.filter((s) => s.status === "active").length}
          </p>
        </div>
        <div className="p-4 border rounded-lg">
          <h4 className="font-semibold mb-1">Total Capacity</h4>
          <p className="text-2xl font-bold">
            {studios.reduce(
              (sum, s) => sum + (s.capacity || s.capacity_max || 0),
              0,
            )}
          </p>
        </div>
        <div className="p-4 border rounded-lg">
          <h4 className="font-semibold mb-1">Avg. Utilization</h4>
          <p className="text-2xl font-bold">73%</p>
        </div>
      </div>

      {/* Studios Display */}
      {filteredStudios.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-4">
            {searchQuery || statusFilter !== "all"
              ? "No studios found matching your criteria."
              : "No studios have been added yet."}
          </p>
          {canAdd && !searchQuery && statusFilter === "all" && (
            <Button onClick={onAddNew}>
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Studio
            </Button>
          )}
        </div>
      ) : viewMode === "grid" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredStudios.map((studio) => (
            <StudioCard
              key={studio.id}
              studio={studio}
              venueId={venueId}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              canEdit={canEdit}
              canDuplicate={canDuplicate}
              canDelete={canDelete}
            />
          ))}
        </div>
      ) : (
        <DataTable
          data={filteredStudios}
          columns={tableColumns}
          emptyMessage="No studios found"
        />
      )}
    </div>
  );
}
